/**
 * Where the knowledge map sits on screen, as fractions of the viewport (the
 * water fills it), not of the map's canvas, which stops short of the nav rail
 * below 1300px. The cloud sits below mid-screen. The water's ripples still
 * spread from a point at the horizontal centre, 19.7% up from the bottom:
 * opening the map ripples there and the cloud rises out of it.
 */
export const WATER_EMANATION_FROM_BOTTOM = 0.197;
/**
 * Where the cloud's centre (the orbit target) projects: a little below
 * mid-screen, so the top of the cloud clears the search field.
 */
export const CLOUD_CENTRE_FROM_BOTTOM = 0.39;
/**
 * How far from that centre the cloud's 90th-percentile radius reaches at the
 * opening distance, as a share of the viewport height. This sets the opening
 * zoom: at 0.51 the whole cloud fills roughly four fifths of the screen
 * height (Jake's chosen zoom, about 1.7x the earlier 0.30). Narrow canvases
 * still back off so the cloud fits between the sides.
 */
export const CLOUD_REACH = 0.51;
/** The share of the half-width beside the centre the cloud may fill. */
const HORIZONTAL_FILL = 0.9;

export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ViewFraming {
  /**
   * `PerspectiveCamera.setViewOffset` arguments: a virtual full view whose
   * centre (where the orbit target projects) is the cloud's centre on the
   * viewport, with the canvas as the sub-view rendered.
   */
  fullWidth: number;
  fullHeight: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  /** The camera aspect for that full view. */
  aspect: number;
  /** The orbit distance at which a cloud of the given radius fits. */
  fitDistance: number;
  /**
   * Room either side of the centre within the band (and the canvas), in
   * viewport pixels: what the Timeline's axis may span.
   */
  bandHalfWidth: number;
  /** Viewport pixels per unit of tan(angle off the view axis). */
  pixelsPerTan: number;
}

/**
 * The off-centre projection that puts the orbit target at the cloud's
 * centre on the viewport, and the distance at which a cloud of `radius`
 * reaches CLOUD_REACH of the viewport height, or fits across its band when
 * that is narrower.
 *
 * The band is the viewport less `leftInset` (the nav rail) and `rightInset`
 * (the topic card's column): the cloud centres in it, so it sits evenly
 * between the two. Equal insets (or none) centre it on the viewport.
 */
export const computeViewFraming = ({
  canvas,
  viewport,
  fov,
  radius,
  leftInset = 0,
  rightInset = 0,
}: {
  canvas: ScreenRect;
  viewport: { width: number; height: number };
  /** Vertical field of view, in degrees. */
  fov: number;
  radius: number;
  /** Viewport pixels taken on the left (the nav rail's right edge). */
  leftInset?: number;
  /** Viewport pixels taken on the right (the card's column). */
  rightInset?: number;
}): ViewFraming => {
  const bandLeft = Math.max(0, leftInset);
  const bandRight = Math.max(bandLeft + 1, viewport.width - rightInset);
  const centreX = (bandLeft + bandRight) / 2;
  // The full view spans [0, 2 × centreX] in viewport pixels, so its centre,
  // where the orbit target projects, is the band's middle.
  const centreY = viewport.height * (1 - CLOUD_CENTRE_FROM_BOTTOM);
  const fullWidth = centreX * 2;
  const fullHeight = centreY * 2;
  const tanHalfFov = Math.tan((fov * Math.PI) / 360);
  // Pixels on screen per unit of tan(angle off the view axis).
  const pixelsPerTan = centreY / tanHalfFov;
  const verticalRoom = viewport.height * CLOUD_REACH;

  // Half the band, and never past the canvas either side.
  const bandHalfWidth = Math.min(
    (bandRight - bandLeft) / 2,
    centreX - canvas.left,
    canvas.left + canvas.width - centreX
  );

  const horizontalRoom = bandHalfWidth * HORIZONTAL_FILL;
  const room = Math.max(1, Math.min(verticalRoom, horizontalRoom));
  // A sphere of `radius` at distance d subtends asin(radius / d).
  const fitDistance = radius / Math.sin(Math.atan(room / pixelsPerTan));

  return {
    fullWidth,
    fullHeight,
    offsetX: canvas.left,
    offsetY: canvas.top,
    width: canvas.width,
    height: canvas.height,
    aspect: fullWidth / fullHeight,
    fitDistance,
    bandHalfWidth,
    pixelsPerTan,
  };
};

/**
 * A viewport point (x right, y up, 0..1) in the canvas's normalised device
 * coordinates, for `Vector3.unproject`.
 */
export const viewportPointToNdc = (
  point: { x: number; y: number },
  canvas: ScreenRect,
  viewport: { width: number; height: number }
): { x: number; y: number } => {
  const clientX = point.x * viewport.width;
  const clientY = (1 - point.y) * viewport.height;

  return {
    x: ((clientX - canvas.left) / canvas.width) * 2 - 1,
    y: 1 - ((clientY - canvas.top) / canvas.height) * 2,
  };
};
